import { useState } from 'react';
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import { Container, Nav, Navbar, NavDropdown } from "react-bootstrap";

import CirclePending from './Icons/CirclePending';
import Gear from './Icons/Gear';
import LogOut from './Icons/LogOut';
import People from './Icons/People';
import Person from './Icons/Person';

import MockUserModal from './MockUserModal';

import UserInfoModal from './UserInfoModal';

import {
    hasRuleEditorAccess,
    hasAnyAdminAccess,
    hasQualityAdminAccess,
    hasTrainingTabAccess,
    userCanViewStandardWorkTrainer,
    userCanViewWorkdayTopics,
    userCanViewWorkdayCertifications,
    hasMockUserAccess,
    isDev,
    isTest,
} from '../functions/AccessHelpers';

import logo from '../images/logo.png'
import EmployeeInfo from "../types/Employee/EmployeeInfo";

type NavbarTypes = {
    userData: EmployeeInfo,
    targetUserData: EmployeeInfo,
    gettingUserData: boolean,
    switchTargetUser: (selectedUserEmail: string) => void,
    logOutAsUser: () => void,
};

/**
 * @param loading indicates if the data is still loading and shows a loading icon if true
 * @returns An "info" component to show on the user dropdown menu
 */
const infoItem = (loading: boolean) => (
    <div className='row'>
        <div className='col-2'>
            { loading ? <CirclePending size='14' animate={loading} /> : <Gear size='14' /> }
        </div>
        <div className='col text-blattner-blue'>
            Info
        </div>
    </div>
);

/**
 * @returns A 'log in as' component to show on the user dropdown menu
 */
const logInAsItem = () => (
    <div className='row'>
        <div className='col-2'>
            <People size='14' />
        </div>
        <div className='col text-blattner-blue'>
            Log In As
        </div>
    </div>
);

/**
 * @returns A 'switch to original user' component to show on the user dropdown menu
 */
const switchToOriginalUser = () => (
    <div className='row'>
        <div className='col-2'>
            <Person size='14' />
        </div>
        <div className='col text-blattner-blue'>
            Go To Original User
        </div>
    </div>
);

/**
 * @returns A 'log out' component to show on the user dropdown menu
 */
const logOutItem = () => (
    <div className='row'>
        <div className='col-2'>
            <LogOut />
        </div>
        <div className='col text-blattner-blue'>
            Log Out
        </div>
    </div>
);

const NavbarTop = ({ userData, targetUserData, gettingUserData, switchTargetUser, logOutAsUser }: NavbarTypes) => {
    const { instance } = useMsal();
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const [showLoginInAsModal, setShowLogInAsModal] = useState<boolean>(false);
    const [page, setPage] = useState<string>('home');

    const currentUser = targetUserData.employeeID === 0 ? userData : targetUserData;

    return (
        <Navbar style={{backgroundColor: "#02568a"}} collapseOnSelect fixed="top" expand="sm" variant="dark">
            <Container>
                <UserInfoModal
                    userData={userData}
                    targetUserData={targetUserData}
                    showModal={showInfoModal}
                    closeInfoModal={() => setShowInfoModal(false)}
                />
                <MockUserModal
                    showModal={showLoginInAsModal}
                    closeInfoModal={() => setShowLogInAsModal(false)}
                    userSelected={(e) => switchTargetUser(e)}
                />
                <Navbar.Toggle aria-controls="responsive-navbar-nav" />
                <Navbar.Collapse id="responsive-navbar-nav">
                    <Navbar.Brand
                        as={Link}
                        to="/"
                        onClick={() => setPage('home')}
                    >
                        <img
                            src={logo}
                            width="200"
                            height="75"
                            alt=""
                        />
                    </Navbar.Brand>
                    <Nav className="me-auto">
                        <Nav.Link
                            className={page === 'home' ? 'border-bottom' : ''}
                            as={Link}
                            to="/"
                            style={{color: "white"}}
                            onClick={() => setPage('home')}
                        >
                            Home
                        </Nav.Link>
                        <Nav.Link
                            className={page === 'tasks' ? 'border-bottom' : ''}
                            as={Link}
                            to="/tasks"
                            style={{color: "white"}}
                            href="#Forms"
                            onClick={() => setPage('tasks')}
                        >
                            NCR Tasks
                        </Nav.Link>
                        <NavDropdown
                            className={page === 'dotCoordinators' || page === 'qualityFormApprovers' ? 'border-bottom' : ''}
                            title="Roles"
                            id="nav-dropdown"
                        >
                            <NavDropdown.Item
                                as={Link}
                                to="/dotcoordinators"
                                onClick={() => setPage('dotCoordinators')}
                            >
                                DOT Coordinators
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/qualityformapprovers"
                                onClick={() => setPage('qualityFormApprovers')}
                            >
                                Quality Form Approvers
                            </NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown
                            className={page === 'qualityControl' || page === 'benchmarkCreationGoal' ? 'border-bottom' : ''}
                            title="Project Goals"
                            id="nav-dropdown"
                        >
                            <NavDropdown.Item
                                as={Link}
                                to="/projectGoals/benchmarkCreationGoal"
                                onClick={() => setPage('benchmarkCreationGoal')}
                            >
                                Benchmark Creation Goals
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/projectGoals/qualityControl"
                                onClick={() => setPage('qualityControl')}
                            >
                                Quality Control
                            </NavDropdown.Item>
                        </NavDropdown>
                        <Nav.Link
                            className={page === 'dataEditor' ? 'border-bottom' : ''}
                            as={Link}
                            to="/dataEditor"
                            style={{color: "white"}}
                            href="#Forms"
                            onClick={() => setPage('dataEditor')}
                        >
                            Data Editor
                        </Nav.Link>
                        {
                            hasTrainingTabAccess(targetUserData) &&
                            <NavDropdown
                                className={page === 'training' ? 'border-bottom' : ''}
                                title='Training'
                                id='nav-dropdown'
                            >
                                {
                                    userCanViewStandardWorkTrainer(currentUser)
                                    &&
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/training/StandardWorkTrainer"
                                        onClick={() => setPage('training')}
                                    >
                                       Certified Standard Work Trainers
                                    </NavDropdown.Item>
                                }
                                {
                                    userCanViewWorkdayCertifications(currentUser)
                                    &&
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/training/Certificates"
                                        onClick={() => setPage('training')}
                                    >
                                       Standard Work Trainer Certificates
                                    </NavDropdown.Item>
                                }
                                {
                                    userCanViewWorkdayTopics(currentUser)
                                    &&
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/training/TrainingTopics"
                                        onClick={() => setPage('training')}
                                    >
                                       Training Topics
                                    </NavDropdown.Item>
                                }
                            </NavDropdown>
                        }
                        {
                            hasQualityAdminAccess(currentUser) &&
                            <NavDropdown
                                className={page === 'benchmarkActivities' ? 'border-bottom' : ''}
                                title="Quality Admin"
                                id="nav-dropdown"
                            >
                                <NavDropdown.Item
                                    as={Link}
                                    to="/qualityAdmin/benchmarkActivities"
                                    onClick={() => setPage('benchmarkActivities')}
                                >
                                    Benchmark Activities
                                </NavDropdown.Item>
                            </NavDropdown>
                        }
                        {
                            hasAnyAdminAccess(currentUser)
                            &&
                            <NavDropdown
                                className={page === 'admin' ? 'border-bottom' : ''}
                                title='Admin'
                                id='nav-dropdown'
                            >
                                {
                                    hasRuleEditorAccess(currentUser)
                                    &&
                                    <NavDropdown.Item
                                        as={Link}
                                        to="/ruleEditor"
                                        onClick={() => setPage('admin')}
                                    >
                                        Data Source Rule Editor
                                    </NavDropdown.Item>
                                }
                                <NavDropdown.Item
                                    as={Link}
                                    to="/admin"
                                    onClick={() => setPage('admin')}
                                >
                                    Admin Page
                                </NavDropdown.Item>
                            </NavDropdown>
                        }
                    </Nav>
                    <div className="WelcomeDiv" >
                        <h5>
                            <NavDropdown title={`Welcome ${currentUser.lastName}, ${currentUser.firstName}`} id="nav-dropdown">
                                <NavDropdown.Item
                                    title='Show current users data.'
                                    onClick={() => setShowInfoModal(!gettingUserData)}
                                >
                                    {infoItem(gettingUserData)}
                                </NavDropdown.Item>
                                {
                                    hasMockUserAccess(userData)
                                    &&
                                    <NavDropdown.Item
                                        title='Log in as another user'
                                        onClick={() => setShowLogInAsModal(!gettingUserData)}
                                        disabled={gettingUserData}
                                    >
                                        {logInAsItem()}
                                    </NavDropdown.Item>
                                }
                                {
                                    userData.employeeID !== targetUserData.employeeID &&
                                    <NavDropdown.Item
                                        title='Log out of other user'
                                        onClick={() => logOutAsUser()}
                                        disabled={gettingUserData}
                                    >
                                        {switchToOriginalUser()}
                                    </NavDropdown.Item>
                                }
                                <NavDropdown.Item
                                    title='Log out of Blattner Portal'
                                    onClick={() => instance.logout()}
                                >
                                    {logOutItem()}
                                </NavDropdown.Item>
                            </NavDropdown>
                        </h5>
                    </div>
                </Navbar.Collapse>
            </Container>
      </Navbar>
    );
}

export default NavbarTop